import {
  enumBoxWidthMode,
  enumBranchStyle,
  enumContainerType,
  enumHorzCentering,
  enumObjectTypes,
  enumSubChartFlag,
  enumVertCentering,
  kHorzBoxSpacing
} from "../constants";
import { GetObjectRecordID } from "../utils/GetObjectRecordID";
import { GetSCArrowHeight } from "../utils/GetSCArrowHeight";
import { GetSCArrowWidth } from "../utils/GetSCArrowWidth";
import { QPNConnectingLine } from "./QPNConnectingLine";
import { QRect } from "./QRect";
import { Globals } from "./Globals";
import { GetDottedID } from "../utils/GetDottedID";
import { GetObjectID } from "../utils/GetObjectID.js";

export class QPageNode {
  constructor() {
    this.fLayout = null;
    this.fDataObj = null;
    this.fDataObjID = null;
    this.fChartBox = null;
    this.fNodeID = Globals._nodeID++;
    //TODO: this increments forever? Never gets cleared?
    this.fPageMgr = null;
    this.fChildLines = null;
    this.fMgrGap = 0;
    this.fLevelingValue = null;
    this.fMasterNode = false;
    this.fGroupList = null;
    this.fDottedRef = null;
    this.fNodeEntity = null; // a reference to node entity in layout manager

    // Multi-Record box params
    this.fContainerNode = null;
    this._containerExplicitlyCleared = false;
  }

  InitNode(pageMgr, layout, dataObj, dataObjID, theChartBox) {
    this.fPageMgr = pageMgr;
    this.fLayout = layout;
    this.fChartBox = theChartBox;
    this.fDataObj = dataObj;

    // fDataObj might be null but we still have a valid fDataObjID (e.g. a free text element)
    this.fDataObjID = dataObjID;

    // This will make sure an MRB is loaded up properly
    this.GetAssociatedNodes();
  }

  GetID() {
    return this.fNodeID;
  }

  GetLayout() {
    return this.fLayout;
  }

  IsInMainSubChart() {
    var dataObj = this.GetDataObj();
    if (dataObj == null) return false;

    var plm = this.fPageMgr;
    var sc = plm.GetCurrentSubChart();
    if (sc == null) return false;

    return sc.IsInMainSubChart(dataObj);
  }

  IsPositionsNode() {
    var dataObj = this.GetDataObj();
    if (dataObj == null) return false;

    return dataObj.IsPositionObj();
  }

  GetDataObj() {
    return this.fDataObj;
  }

  GetDataObjID() {
    return this.fDataObjID;
  }

  GetRootDataObj() {
    if (this.fDataObj) return this.fDataObj;
    else if (this.fChartBox) return this.fChartBox.GetRootDataObj();
  }

  GetDottedID() {
    return GetDottedID(this.GetDataObj());
  }

  GetObjectRecordID() {
    return GetObjectRecordID(this.GetDataObj());
  }

  GetObjectID() {
    return GetObjectID(this.GetDataObj());
  }

  GetChartBox() {
    return this.fChartBox;
  }

  GetSubChartFlag() {
    var l = this.GetLayout();
    return l.GetSubChartFlag();
  }

  GetNumChildren() {
    var dataObj = this.GetDataObj();
    if (dataObj == null) return 0;

    //var l = this.GetLayout();
    var scFlag = this.GetSubChartFlag(); // l.GetSubChartFlag();
    if (scFlag == enumSubChartFlag.drilldown) return 0;

    if (
      this.GetContainerNode() &&
      this.GetContainerNode().GetSubChartFlag() == enumSubChartFlag.drilldown &&
      !this.IsPositionsNode()
    )
      return 0;

    var l = this.GetLayout();
    var isCollapsed = l.GetCollapsed();
    if (isCollapsed) return 0;

    if (this.GetBranchStyle() === enumBranchStyle.index) return 0;

    return dataObj.GetNumChildren();
  }

  // returns a list of all children nodes that are similar to child aNode from container perspective, i.e. either
  // they belong to the same container, or if aNode is not part of a container, no container at all
  GetSimilarSiblingNodes(aNode) {
    var nodeList = [];
    var numChildren = this.GetNumChildren();
    var nodeContainer = aNode.GetContainerNode();
    for (var i = 0; i < numChildren; i++) {
      var child = this.GetIndexChild(i);
      if (child == null) continue;
      if (child.GetContainerNode() === nodeContainer) {
        nodeList.push(child);
      }
    }

    return nodeList;
  }

  IndexOfChild(aNode) {
    var dataObj = this.GetDataObj();
    var childDataObj = aNode.GetDataObj();
    var index = dataObj.IndexOfChild(childDataObj);
    return index;
  }

  GetParent() {
    var parent = this.fPageMgr.GetParentNode(this);

    return parent;
  }

  GetParentID() {
    var parent = this.GetParent();
    var parID = null;
    if (parent != null) parID = parent.GetID();

    return parID;
  }

  GetBoundingRect() {
    var l = this.GetLayout();
    return l.GetBoundingRect();
  }

  HasShadow() {
    var l = this.GetLayout();
    return l.GetShadowOn();
  }

  GetBorderOutset() {
    var l = this.GetLayout();
    return l.GetBorderOutset();
  }

  GetBaseRect() {
    var borderOutset = this.GetBorderOutset();
    var r = new QRect();
    var sx = -borderOutset;
    var ex = this.GetBoxWidth() + borderOutset;
    var sy = -borderOutset;
    var ey = this.GetBoxHeight() + borderOutset;

    r.InitRect(sx, sy, ex, ey);
    return r;
  }

  GetAdornmentRect() {
    var l = this.GetLayout();
    var scArrowT = l.SCBreakTopAdder();
    var scArrowB = l.SCBreakBottomAdder();
    var borderOutset = this.GetBorderOutset();

    var sx = 0;
    var ex = 0;
    var sy = 0;
    var ey = 0;

    var common = false;

    if (scArrowT !== 0) {
      sy = -borderOutset - scArrowT;
      ey = -borderOutset;
      common = true;
    } else if (scArrowB !== 0) {
      var h = this.GetBoxHeight();
      sy = h + borderOutset;
      ey = sy + scArrowB;
      common = true;
    }

    if (common) {
      var arrowWidth = GetSCArrowWidth();
      var boxWidth = this.GetBoxWidth();
      var halfw = boxWidth / 2;
      var aw = arrowWidth / 2;
      sx = Math.floor(halfw - aw);
      ex = Math.ceil(halfw + aw);
    }

    var r = new QRect();
    r.InitRect(sx, sy, ex, ey);
    return r;
  }

  GetSCArrowInfo() {
    var result = { type: "none", width: 0, height: 0 };
    var boxLayout = this.GetLayout();
    var scArrowTop = boxLayout.SCBreakTopAdder();
    if (scArrowTop !== 0) {
      result.type = "top";
      result.height = GetSCArrowHeight();
      result.width = GetSCArrowWidth();
      return result;
    }

    var scArrowBottom = boxLayout.SCBreakBottomAdder();
    if (scArrowBottom !== 0) {
      result.type = "bottom";
      result.height = GetSCArrowHeight();
      result.width = GetSCArrowWidth();
    }

    return result;
  }

  //=======================================================================================
  // Don't include border
  GetBoxWidth() {
    var l = this.GetLayout();
    return l.GetBoxWidth();
  }

  GetBoxHeight() {
    var l = this.GetLayout();
    var boxH = l.GetBoxHeight(false);
    //var b1 = this.GetBoxHeightForStyle(enumBranchStyle.onecolumnleft);
    //if (boxH != b1)
    //	console.log("Hello!");

    return boxH;
  }

  GetContainerEnvelope() {
    var l = this.GetLayout();
    var env = l.GetContainerEnvelope();
    if (!env) env = { x: 0, y: 0 };
    return env;
  }

  GetBoxHeightForStyle(branchStyle) {
    var l = this.GetLayout();
    return l.GetBoxHeightForStyle(branchStyle);
  }

  GetPositionX() {
    var l = this.GetLayout();
    return l.GetPositionX();
  }

  GetPositionY() {
    var l = this.GetLayout();
    return l.GetPositionY();
  }

  GetContainerCellPadding() {
    var l = this.GetLayout();
    return l.GetBoxPadding();
  }

  GetMemberBoxSpacing() {
    var l = this.GetLayout();
    return l.GetBoxMargins();
  }

  SetPosition(x, y) {
    if (this.fChartBox.fElements) {
      var elems = this.fChartBox.fElements;
      for (var i = 0; i < elems.length; i++) {
        var theElement = elems[i];
        var xPos = x + theElement.fDeltaDefX;
        if (theElement.fHPosition == enumHorzCentering.pinRight) xPos += this.GetBoxWidth();
        var yPos = y + theElement.fDeltaDefY;
        if (theElement.fVPosition == enumVertCentering.pinBottom) yPos += this.GetBoxHeight();
        else if (theElement.fVPosition == enumVertCentering.centermiddle)
          yPos += this.GetBoxHeight() / 2;
        theElement.SetPositionX(xPos);
        theElement.SetPositionY(yPos);
      }
    }
    var l = this.GetLayout();
    //TODO: should there be a return
    return l.SetPosition(x, y);
  }

  SetContainerBoxSize(width, height) {
    var l = this.GetLayout();
    l.fCachedHeight = null;
    l.SetContainerEnvelope({ x: width, y: height });
    if (this.IsPositionsNode()) {
      l.SetWidthMin(width);
      l.SetWidthMax(Number.POSITIVE_INFINITY);
      l.CalculateLayout(false);
      if (l.GetBoxWidth() < width) {
        l.SetBoxWidth(width);
      }
    } else {
      this.SetBoxWidth(width);
    }
  }

  SetBoxWidth(width) {
    var l = this.GetLayout();
    l.fCachedHeight = null;
    l.SetWidthMin(width);
    l.SetWidthMax(width);
    l.CalculateLayout(false);
  }

  GetMgrGap() {
    return this.fMgrGap;
  }

  SetMgrGap(mgrGap) {
    this.fMgrGap = mgrGap;
  }

  GetBranchStyle() {
    var l = this.GetLayout();
    return l.GetBranchStyle();
  }

  SetBranchStyle(branchStyle) {
    //console.log("Setting branch style:", this.fNodeID, "-", branchStyle)
    var l = this.GetLayout();
    return l.SetBranchStyle(branchStyle);
  }

  /**
   * @return {boolean}
   */
  IsSizingToChildren() {
    var l = this.GetLayout();

    return l.GetWidthSizingMode() === enumBoxWidthMode.autochildren;
  }

  IsAutoSmartH() {
    var l = this.GetLayout();
    return l.IsAutoSmartH();
  }

  IsAutoSmartV() {
    var l = this.GetLayout();
    return l.IsAutoSmartV();
  }

  TagAutoSmartH() {
    var l = this.GetLayout();
    return l.TagAutoSmartH(true);
  }

  TagAutoSmartV() {
    var l = this.GetLayout();
    return l.TagAutoSmartV(true);
  }

  IsAssistant() {
    var l = this.GetLayout();
    return l.IsAssistant();
  }

  TransferBranchStyle(srcNode) {
    var l = this.GetLayout();
    var srcLayout = srcNode.GetLayout();
    l.TransferBranchStyle(srcLayout);
  }

  GetColumnIndex() {
    var l = this.GetLayout();
    return l.GetColumnIndex();
  }

  SetColumnIndex(columnIndex) {
    var l = this.GetLayout();
    return l.SetColumnIndex(columnIndex);
  }

  GetRowPairMinBoxHeight() {
    var l = this.GetLayout();
    return l.GetRowPairMinBoxHeight();
  }

  SetRowPairMinBoxHeight(minHeight) {
    var l = this.GetLayout();
    return l.SetRowPairMinBoxHeight(minHeight);
  }

  //============================================================================
  // Vertical Spacing Control
  GetBoxMargins() {
    var l = this.GetLayout();
    return l.GetBoxMargins();
  }

  SetBoxMargins(boxMargins) {
    var l = this.GetLayout();
    l.SetBoxMargins(boxMargins);
  }

  GetBoxSpacing() {
    var l = this.GetLayout();
    return l.GetBoxSpacing();
  }

  GetMgrVSpacing1() {
    var l = this.GetLayout();
    return l.GetMgrVSpacing1();
  }

  GetMgrVSpacing2() {
    var l = this.GetLayout();
    return l.GetMgrVSpacing2();
  }

  SetMgrVSpacing1(vSpacing) {
    var l = this.GetLayout();
    l.SetMgrVSpacing1(vSpacing);
  }

  SetMgrVSpacing2(vSpacing) {
    var l = this.GetLayout();
    l.SetMgrVSpacing2(vSpacing);
  }

  GetMarginMode() {
    var l = this.GetLayout();
    return l.GetMarginMode();
  }

  SetMarginMode(marginMode) {
    var l = this.GetLayout();
    return l.SetMarginMode(marginMode);
  }

  GetColumnPositions() {
    var l = this.GetLayout();
    return l.GetColumnPositions();
  }

  SetColumnPositions(colRight, colLeft) {
    var l = this.GetLayout();
    return l.SetColumnPositions(colRight, colLeft);
  }

  GetBranchGroup() {
    var l = this.GetLayout();
    return l.GetBranchGroup();
  }

  SetBranchGroup(branchGroup) {
    var l = this.GetLayout();
    return l.SetBranchGroup(branchGroup);
  }

  GetLevelingValue() {
    return this.fLevelingValue;
  }

  SetLevelingValue(levelingValue) {
    this.fLevelingValue = levelingValue;
  }

  Shift(deltaX, deltaY) {
    var posX = this.GetPositionX();
    var posY = this.GetPositionY();
    this.SetPosition(posX + deltaX, posY + deltaY);
  }

  ShiftChildren(deltaX, deltaY) {
    var n = this.GetNumChildren();
    for (var i = 0; i < n; i++) {
      var child = this.GetIndexChild(i);
      child.Shift(deltaX, deltaY);
      child.ShiftChildren(deltaX, deltaY);
    }
  }

  GetIndexChild(index) {
    return this.fPageMgr.GetIndexChildNode(this, index);
  }

  // returns an array of all nodes in the branch (including self) regardless of hierarchy
  GetAllBranchNodes() {
    var allNodes = [];
    allNodes.push(this);

    for (var i = 0; i < this.GetNumChildren(); i++) {
      var childNode = this.GetIndexChild(i);
      allNodes.push(childNode);
    }

    for (i = 0; i < this.GetNumChildren(); i++) {
      childNode = this.GetIndexChild(i);
      if (childNode !== null) {
        var childBranchNodes = childNode.GetAllBranchNodes();
        childBranchNodes.shift();
        allNodes = allNodes.concat(childBranchNodes);
      }
    }

    return allNodes;
  }

  GetConnectorSpacing(leftNode) {
    var spacing = 0;
    var rightBStyle = this.GetBranchStyle();
    switch (rightBStyle) {
      case enumBranchStyle.onecolumnright:
      case enumBranchStyle.onecolumncenterright:
        spacing = kHorzBoxSpacing;
        break;
    }

    if (leftNode != null) {
      var leftBStyle = leftNode.GetBranchStyle();
      switch (leftBStyle) {
        case enumBranchStyle.onecolumncenterleft:
        case enumBranchStyle.onecolumnleft:
          spacing += kHorzBoxSpacing;
          break;
      }
    }

    return spacing;
  }

  ClearLines() {
    this.fChildLines = null;
  }

  AddALine(sx, sy, ex, ey) {
    if (this.fChildLines == null) this.fChildLines = new Array();

    var connLine = new QPNConnectingLine();
    connLine.InitLine(sx, sy, ex, ey);
    this.fChildLines.push(connLine);
    return connLine;
  }

  GetChildLines() {
    return this.fChildLines;
  }

  SetNameValue(newValue) {
    var d = this.fDataObj;
    if (d == null) return;

    var nameDef = d.GetFieldDefByID("Name");
    d.SetFieldValue(nameDef, newValue);
  }

  SetTitleValue(newValue) {
    var d = this.fDataObj;
    if (d == null) return;

    var titleDef = d.GetTitleFieldDef();
    if (titleDef == null) return;

    d.SetFieldValue(titleDef, newValue);
  }

  GetIndex() {
    var p = this.GetParent();
    if (p == null) return 0;

    var pObj = p.GetDataObj();
    var d = this.GetDataObj();
    var index = pObj.IndexOfChild(d);
    return index;
  }

  SetMasterNode(masterNode) {
    this.fMasterNode = masterNode;
  }

  GetMasterNode() {
    return this.fMasterNode;
  }

  //==========================================================================================
  // Dotted-lines
  AddDottedRef(aNode) {
    if (this.fDottedRef == null) this.fDottedRef = [];

    this.fDottedRef.push(aNode);
  }

  NumDotted() {
    if (this.fDottedRef == null) return 0;

    return this.fDottedRef.length;
  }

  GetIndexDottedRef(index) {
    if (this.fDottedRef == null) return null;

    return this.fDottedRef[index];
  }

  SetupDottedID(nodeList) {
    var dottedID = this.GetDottedID();
    if (dottedID == null) return;
    if (this.fDottedRef) {
      this.fDottedRef.length = 0;
    }

    dottedID = dottedID.toString();
    var parts = dottedID.split("|");
    var pLen = parts.length;

    var n = nodeList.length;
    for (var i = 0; i < n; i++) {
      var aNode = nodeList[i];
      var personID = aNode.GetObjectRecordID();
      if (personID == null) {
        personID = aNode.GetObjectID().toString();
        //this.fDottedRef = null;
        //return;
      }

      personID = personID.toString();
      for (var j = 0; j < pLen; j++) {
        if (parts[j] === personID) {
          this.AddDottedRef(aNode);
        }
      }
    }
  }

  SetNodeEntity(nodeEntity) {
    this.fNodeEntity = nodeEntity;
  }

  GetNodeEntity() {
    return this.fNodeEntity;
  }

  //==========================================================================================
  // Box Grouping
  GetBoxHighlightGroups() {
    this.fGroupList = {};

    var layout = this.GetLayout();
    if (layout) {
      var highlightColors = [];
      var highlightGroupInfo = layout.GetHighlightInfo();

      if ("dynamic" in highlightGroupInfo) {
        for (var aKey in highlightGroupInfo["dynamic"]) {
          var obj = {};
          obj[aKey] = highlightGroupInfo["dynamic"][aKey];
          highlightColors.push(obj);
        }
      }

      if ("template" in highlightGroupInfo) {
        for (var aKey in highlightGroupInfo["template"]) {
          var obj = {};
          obj[aKey] = highlightGroupInfo["template"][aKey];
          highlightColors.push(obj);
        }
      }
      for (var aHighlightIdx in highlightColors) {
        var aHighlight = highlightColors[aHighlightIdx];
        for (var theId in aHighlight) this.fGroupList[theId] = aHighlight[theId];
      }
    }

    //TODO: Active highlighting support (outside of formatting)
    return this.fGroupList;
  }

  AddAsGroupMember(groupID) {
    var i = this.fGroupList.indexOf(groupID);
    if (i === -1) {
      this.fGroupList.push(groupID);
    }
  }

  RemoveAsGroupMember(groupID) {
    if (!this.fGroupList) {
      return;
    }

    var i = this.fGroupList.indexOf(groupID);
    if (i > -1) {
      this.fGroupList.splice(i, 1);
    }
  }

  GetTempDottedList() {
    //Until a UI comes into action, we go by naming.
    var dottedList = [];
    var dataObj = this.GetDataObj();
    var fieldDef = dataObj.GetFieldDefByID("Name");
    var name = dataObj.GetFieldValue(fieldDef);
    if (name) {
      var strRef = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
      var valueItems = name.split(",");
      for (var i = 0; i < valueItems.length; i++) {
        var n = strRef.indexOf(valueItems[i]);
        if (n !== -1) {
          dottedList.push(valueItems[i]);
        }
      }
    }

    return dottedList;
  }

  //==========================================================================================
  AssociateNode(childNode) {
    childNode.SetContainerNode(this);
    var dbObjID = childNode.GetObjectID();
    var l = this.GetLayout();
    var newList = l.GetAssociatedObjIDs(true);
    if (newList == null) newList = [];

    newList.push(dbObjID);
    l.SetAssociatedObjIDs(newList);

    this.AssociateChildrenIDs(childNode);
  }

  ClearCachedFieldValueByID(theFieldID) {
    var theDataObj = this.GetDataObj();
    if (theDataObj) {
      theDataObj.ClearCachedFieldValueByID(theFieldID);
    }
  }

  SetContainerNode(containerNode) {
    this.fContainerNode = containerNode;
    //The below code was compensating (poorly) for a failure elsewhere. It has since been corrected.

    if (containerNode) {
      this._containerExplicitlyCleared = false;
      this.GetLayout().SetContainerObjID(containerNode.GetDataObjID());
    } else {
      this._containerExplicitlyCleared = true;
    }
  }

  GetContainerNode() {
    if (this._containerExplicitlyCleared) return null;
    if (this.fContainerNode != null) return this.fContainerNode;
    var containerObjID = this.GetLayout()?.GetContainerObjID();
    if (!containerObjID || !this.fPageMgr) return null;

    var containerNode = this.fPageMgr.FindNodeFromDataObjID(containerObjID);
    if (containerNode) this.fContainerNode = containerNode;

    return this.fContainerNode;
  }

  GetAssociatedNodes() {
    var l = this.GetLayout();
    var idList = l.GetAssociatedObjIDs(false);

    var n = idList == null ? 0 : idList.length;
    var plm = this.fPageMgr;
    var assocNodes = [];
    for (var i = 0; i < n; i++) {
      var matchNode = plm.FindNodeFromDataObjID(idList[i]);
      if (!matchNode) {
        //CB 2020-03-26 - Now that the container system has matured- bypassing this problem.
        //Incredibly difficult to manage in conjunction with the filtering system.
        continue;
        //console.log('warning');
      }
      assocNodes.push(matchNode);
      matchNode.SetContainerNode(this);
    }

    return assocNodes;
  }

  UnassociateNode(childNode) {
    childNode.SetContainerNode(null);
    var l = this.GetLayout();
    var dbObjID = childNode.GetObjectID();
    l.UnassociateNode(dbObjID);

    this.RemoveAssociateChildrenIDs(childNode);
  }

  ClearAssociatedNodes() {
    var clearedNodes = [];
    var assocNodes = this.GetAssociatedNodes();
    var n = assocNodes.length;
    for (var i = 0; i < n; i++) {
      var aNode = assocNodes[i];
      this.UnassociateNode(aNode);
      clearedNodes.push(aNode);
    }

    return clearedNodes;
  }

  SetContainerNumCols(numCols) {
    var l = this.GetLayout();
    l.SetContainerNumCols(numCols);
  }

  GetContainerNumCols() {
    var l = this.GetLayout();
    return l.GetContainerNumCols();
  }

  SetContainerMaxRows(numRows) {
    var l = this.GetLayout();
    l.SetContainerMaxRows(numRows);
  }

  GetContainerMaxRows() {
    var l = this.GetLayout();
    return l.GetContainerMaxRows();
  }

  SetContainerType(containerType) {
    var l = this.GetLayout();
    l.SetContainerType(containerType);
  }

  GetContainerType() {
    var l = this.GetLayout();
    return l.GetContainerType();
  }

  SetContainerTopLinesToColumns(doLines) {
    var l = this.GetLayout();
    l.SetContainerTopLinesToColumns(doLines);
  }

  GetContainerTopLinesToColumns() {
    var l = this.GetLayout();
    return l.GetContainerTopLinesToColumns();
  }

  SetContainerBottomLinesToColumns(doLines) {
    var l = this.GetLayout();
    l.SetContainerBottomLinesToColumns(doLines);
  }

  GetContainerBottomLinesToColumns() {
    var l = this.GetLayout();
    return l.GetContainerBottomLinesToColumns();
  }

  SetContainerInternalLinesToColumns(doLines) {
    var l = this.GetLayout();
    l.SetContainerInternalLinesToColumns(doLines);
  }

  GetContainerInternalLinesToColumns() {
    var l = this.GetLayout();
    return l.GetContainerInternalLinesToColumns();
  }

  // adds all children of aNode to list of references to children of this.
  // this is a Container
  AssociateChildrenIDs(aNode) {
    var layout = this.GetLayout();

    var numChildren = aNode.GetNumChildren();
    for (var i = 0; i < numChildren; i++) {
      var child = aNode.GetIndexChild(i);
      if (child == null) {
        console.log("Potential failure - index child mismatch");
        continue;
      }
      layout.AddAssociatedChildID(child.GetObjectID());
    }
  }

  //=======================================================================
  // Things related to container outside children ordering. Used by PageOptimization
  RemoveAssociateChildrenIDs(aNode) {
    var layout = this.GetLayout();
    var numChildren = aNode.GetNumChildren();
    for (var i = 0; i < numChildren; i++) {
      var child = aNode.GetIndexChild(i);
      //Note- it's totally fine to not have children. Subchart breaks or parking lot can remove children from active page.
      if (child) layout.RemoveAssociatedChildID(child.GetObjectID());
    }
  }

  UpdateAssociateChildrenIDs(childrenIDs) {
    var layout = this.GetLayout();
    layout.ReplaceAssociatedChildrenIDs(childrenIDs);
  }

  GetOrderedChildrenIDs() {
    var layout = this.GetLayout();
    return layout.GetAssociatedChildrenIDs();
  }

  GetNodeBandLevel() {
    var theChartBox = this.GetChartBox();
    var objLevel = -1;
    if (theChartBox.GetContainerType() == enumContainerType.nonmrb) {
      var dataObject = theChartBox.GetDataObj();
    } else {
      dataObject = theChartBox.GetRootDataObj();
    }
    if (dataObject) {
      var plm = this.fPageMgr;
      var levelFieldID = plm.GetLevelField();
      if (levelFieldID) {
        var mgmtLevelDef = dataObject.GetFieldDefByName(levelFieldID);
        if (mgmtLevelDef) {
          if (dataObject.GetFieldValue(mgmtLevelDef))
            objLevel = parseInt(dataObject.GetFieldValue(mgmtLevelDef));
          /// This is wrong - isNaN should be used, but this could introduce new issues changing now..
          if (objLevel == NaN) objLevel = -1;
        }
      }
    }

    return { x: 1, y: objLevel };
  }
}
